import { IDashboardSettings } from "./dashboardConfig";
import { PlanType } from "./orders";
import { ReturnSymbol } from "./performance";
import {HoldingGraphType} from "./holding-graph-type";


export interface IRegistrationSettings {
    enabled: boolean;
    showRegisterLinkOnLogin: boolean;
    showSourceOfWealth: boolean;
    showDisclaimer: boolean;
    signUpTypes: Array<number>;
    showClientAgreement: boolean;
    custodianBlurb: string;
    welcomeTitle: string;
    welcomeBlurb: string;
    component: string;
}
export const defaultRegistrationSettings: IRegistrationSettings = {
    enabled: false,
    showRegisterLinkOnLogin: false,
    showSourceOfWealth: false,
    showDisclaimer: false,
    signUpTypes: [],
    showClientAgreement: false,
    custodianBlurb: "",
    welcomeTitle: "",
    welcomeBlurb: "",
    component: ""
}

export interface IKiwiSaverRegistrationSettings {
    blurb: string;
    header: string;
    registrationCompleteHeader: string;
    registrationCompleteBlurb: string;
}

export const defaultKiwiSaverRegistrationSettings: IKiwiSaverRegistrationSettings = {
    blurb: "",
    header: "",
    registrationCompleteHeader: "",
    registrationCompleteBlurb: ""
}

export interface ISignInSettings {
    enabled: boolean;
    loginWelcomeText: string;
    TFAPlaceHolderText: string;
    createNewAccountLinkText: string;
}
export const defaultSignInSettings: ISignInSettings= {
    enabled: true,
    loginWelcomeText: "Welcome back",
    TFAPlaceHolderText: "Enter authorisation code",
    createNewAccountLinkText: ""
}

export interface IBuySellFeatureSetting {
    enabled: boolean;
    allowInvestor: boolean;
    allowOrderCreation: boolean;
    allowBuying: boolean;
    showPendingInvestments: boolean;
    allowCancelOrder: boolean;
    buySellLearnMore: string;
    buySellText: string;
    regularInvestmentPlanTabName: string;
    allowMultipleCurrencies: boolean;
    showSecurityGrowthGraph: boolean;
    equityBuysConvertToUnitsLater: boolean;
    minimumBuyAmount: number;
}
export const defaultBuySellFeatureSettings: IBuySellFeatureSetting = {
    enabled: false,
    allowInvestor: false,
    allowOrderCreation: false,
    allowBuying: false,
    showPendingInvestments: false,
    allowCancelOrder: false,
    buySellLearnMore: "",
    buySellText: "",
    regularInvestmentPlanTabName: "",
    allowMultipleCurrencies: false,
    showSecurityGrowthGraph: false,
    equityBuysConvertToUnitsLater: true,
    minimumBuyAmount: null
}

export interface IWithdrawalFeatureSetting {
    enabled: boolean;
    allowInvestor: boolean;
    allowManager: boolean;
    referenceRequired: boolean;
    withdrawalBlurb: string;
}
export const defaultWithdrawalFeatureSettings: IWithdrawalFeatureSetting = {
    enabled: true,
    allowInvestor: false,
    allowManager: true,
    referenceRequired: true,
    withdrawalBlurb: ""
}

export interface ITermDepositsFeatureSettings {
    enabled: boolean;
    allowInvestor: boolean;
    termDepositText: string;
    linkText: string;
    marginText: string;
    showMinimums: boolean;
    showOpenTermDepositsTitle: boolean;
    currentTermDepositText: string;
}

export const defaultTermDepositFeatureSettings: ITermDepositsFeatureSettings = {
    enabled: false,
    allowInvestor: false,
    termDepositText: null,
    linkText: null,
    marginText: null,
    showMinimums: false,
    showOpenTermDepositsTitle: false,
    currentTermDepositText: null
}

export interface IAvailableCashSettings {
    availableCashText: string;
}

export const defaultAvailableCashSettings: IAvailableCashSettings = {
    availableCashText: "Available"
}

export interface IDepositFeatureSettings {
    enabledManagers: IManagerDepositFeatureSetting[];
}
export const defaultDepositFeatureSettings: IDepositFeatureSettings = {
    enabledManagers: []
}

export interface IManagerDepositFeatureSetting {
    managerId: number;
    accountNumber: string;
    accountName: string;
    companyName: string;
}
export const defaultManagerDepositFeatureSetting: IManagerDepositFeatureSetting = {
    managerId: 0,
    accountNumber: "",
    accountName: "",
    companyName: ""
}

export interface ITransferFeatureSetting {
    enabled: boolean;
}
export const defaultTransferFeatureSetting: ITransferFeatureSetting = {
    enabled: false
}

export interface IRegularInvestmentPlanSetting {
    enabled: boolean;
    type: keyof typeof PlanType;
    minimumAmount: number;
    frequencies: Array<number>;
    name: string;
    planLabel: string;
    chooseFundsName: string;
    tradeNote: string;
    header: string;
    updateHeader: string;
    createHeader: string;
    schemePdsLink: string;
    pdsConfirmationRequired: boolean;
    pdsAcknowledgmentBlurb: string;
    kiwisaverInvestorsRequiredToSetUpRip?: boolean;
    feesAndChargesLink: string;
    customFeeWording: string;
    getStartedInvestingLink: string;
    investmentStrategyMoreInformationLink: string;
    chooseFundsMoreInformationLink: string;
    fundsMoreInformationLink: string;
    sectorFilterEnabled: boolean,
    managerFilterEnabled: boolean
}
export const defaultRegularInvestmentPlan: IRegularInvestmentPlanSetting = {
    enabled: false,
    type: "ExplicitAmount",
    minimumAmount: 0,
    frequencies: [],
    name: "",
    planLabel: "",
    chooseFundsName: "",
    tradeNote: "",
    header: "",
    updateHeader: "",
    createHeader: "",
    schemePdsLink: "",
    pdsConfirmationRequired: false,
    pdsAcknowledgmentBlurb: "",
    feesAndChargesLink: "",
    customFeeWording: "",
    getStartedInvestingLink: "",
    investmentStrategyMoreInformationLink: "",
    chooseFundsMoreInformationLink: "",
    fundsMoreInformationLink: "",
    sectorFilterEnabled: true,
    managerFilterEnabled: true
}

export interface IHeaderSettings {
    options: {
        showProfileEntityId: boolean;
    },
    navigation: {
        financialActionsEnabled: boolean;
    }
}
export const defaultHeaderSettings: IHeaderSettings = {
    options: {
        showProfileEntityId: false
    },
    navigation: {
        financialActionsEnabled: true
    }
}

export interface IFooterSettings {
    enabled: boolean;
    copyright: {
        enabled: boolean;
    };
}
export const defaultFooterSettings: IFooterSettings = {
    enabled: false,
    copyright: {
        enabled: false
    }
}

export interface IPerformanceFeatureSettings {
    enabled: boolean;
    initialSpanMonths: number; //Sets the initial span months for performance return data
    defaultReturnSymbol: ReturnSymbol, //Sets the default symbol for performance returns ('Dollar' or 'Percentage')
    xirrTooltipText: string;
    twrTooltipText: string;
  allowEntitySelection: boolean;
}
export const defaultPerformanceFeatureSettings: IPerformanceFeatureSettings = {
    enabled: false,
    initialSpanMonths: 0,
    defaultReturnSymbol: ReturnSymbol.PERCENT,
    xirrTooltipText: "",
  twrTooltipText: "",
  allowEntitySelection: true
}

export interface IReportFeatureSettings {
    onlyAllowSelectedInvestorsOnSelection: boolean;
    allHoldingsReportEnabled: boolean;
    generalLedgerReport: boolean;
    investmentReport: boolean;
    investorListReportEnabled: boolean;
    managementAccounts: boolean;
    memberListReportEnabled: boolean;
    portfolioPerformanceReportEnabled: boolean;
    taxSummaryReportEnabled: boolean;
    apsReportEnabled: boolean;
    capitalMovementsReport: boolean;
}
export const defaultReportFeatureSettings: IReportFeatureSettings = {
    onlyAllowSelectedInvestorsOnSelection: false,
    allHoldingsReportEnabled: false,
    generalLedgerReport: false,
    investmentReport: true,
    investorListReportEnabled: false,
    managementAccounts: false,
    memberListReportEnabled: false,
    portfolioPerformanceReportEnabled: false,
    taxSummaryReportEnabled: false,
    apsReportEnabled: false,
    capitalMovementsReport: false
}

export interface IInvestorProfileFeatureSettings {
    enabled: boolean,
    allowInvestor: boolean,
    enabledManagers: any[],
    allowEdit: boolean,
    customerDueDiligenceEnabled: boolean,
};

export interface IFeatureSettings {
    universalLogin: {
      enabled: boolean;
    },
    clientHome: string;
    oldView: {
        enabled: boolean;
    },
    summary: {
        enabled: boolean;
    }
    modelPortfolios: {
        enabled: boolean;
        onlyManagedPortfolios: boolean;
    },
    holdings: {
        enabled: boolean;
        holdingGraphType: HoldingGraphType;
    },
    transactions: {
        enabled: boolean;
    },
    cashflow: {
        enabled: boolean;
        hideCashAvailable?: boolean;
    },
    documents: {
        enabled: boolean;
        allowInvestor: boolean;
    },
    documentLibrary: {
        enabled: boolean;
        allowInvestor: boolean;
    },
    performance: {
        enabled: boolean;
        initialSpanMonths: number;
        defaultReturnSymbol: ReturnSymbol;
      allowEntitySelection: boolean;
    },
    notifications: {
        enabled: boolean;
    },
    reports: {
        enabled: boolean;
        allowInvestor: boolean;
    },
    assetPerformance: {
        enabled: boolean;
        assetClassificationEnabled: boolean;
        contributionToReturnEnabled: boolean;
    },
    signIn: ISignInSettings,
    registration: IRegistrationSettings,
    kiwiSaverRegistration: IKiwiSaverRegistrationSettings,
    dashboard: IDashboardSettings,
    buySell: IBuySellFeatureSetting,
    termDeposits: ITermDepositsFeatureSettings,
    availableCashSettings: IAvailableCashSettings,
    accountSettings: {
        showProfileTab: boolean;
    },
    investorProfile: IInvestorProfileFeatureSettings,
    addressDetails: {
        allowInvestor: boolean;
    },
    taxDetails: {
        enabled: boolean;
        allowInvestor: boolean;
        taxDetailsUpdateMessge: string;
    },
    dateSelector: {
        enabled: boolean;
        disableAggregation: boolean;
    },
    regularInvestmentPlan: IRegularInvestmentPlanSetting,
    withdrawals: IWithdrawalFeatureSetting,
    googleTagManager: {
        enabled: boolean;
    },
    raygun: {
        enabled: boolean;
        raygunApiKey: string;
    },
    footer: IFooterSettings,
    header: IHeaderSettings,
    deposits: IDepositFeatureSettings,
    transfers: ITransferFeatureSetting
    publicHeader:{
        enabled: boolean;
    };
}


export const defaultFeatureConfig: IFeatureSettings = {
    clientHome: "/feature/client/dashboard",
    oldView: {
        enabled: false
    },
    summary: {
        enabled: false
    },
    modelPortfolios: {
        enabled: true,
        onlyManagedPortfolios: false
    },
    holdings: {
        enabled: true,
        holdingGraphType: HoldingGraphType.Price
    },
    transactions: {
        enabled: true
    },
    documents: {
        enabled: true,
        allowInvestor: true
    },
    cashflow: {
        enabled: true
    },
    documentLibrary: {
        enabled: true,
        allowInvestor: true
    },
    performance: {
        enabled: false,
        initialSpanMonths: 0,
        defaultReturnSymbol: ReturnSymbol.PERCENT,
        allowEntitySelection: true
    },
    reports: {
        enabled: false,
        allowInvestor: false
    },
    notifications: {
        enabled: true
    },
    assetPerformance: {
        enabled: true,
        assetClassificationEnabled: true,
        contributionToReturnEnabled: true,
    },
    signIn: defaultSignInSettings,
    registration: defaultRegistrationSettings,
    kiwiSaverRegistration: defaultKiwiSaverRegistrationSettings,
    dashboard: {
        enabled: false,
        dollarsVsPercentEnabled: false,
        onboarding: {
            enabled: false,
            blurb: ''
        },
        widgets: [],
        widgetLayout: {
            class: "",
            children: []
        }
    },
    buySell: defaultBuySellFeatureSettings,
    accountSettings: {
        showProfileTab: true
    },
    investorProfile: {
        enabled: false,
        allowInvestor: false,
        enabledManagers: [],
        allowEdit: false,
        customerDueDiligenceEnabled: false,
    },
    addressDetails: {
        allowInvestor: false
    },
    taxDetails: {
        enabled: false,
        allowInvestor: true,
        taxDetailsUpdateMessge: 'If you would like to request a change please contact your adviser or manager.'
    },
    dateSelector: {
        enabled: true,
        disableAggregation: false
    },
    regularInvestmentPlan: defaultRegularInvestmentPlan,
    withdrawals: defaultWithdrawalFeatureSettings,
    termDeposits: defaultTermDepositFeatureSettings,
    availableCashSettings: defaultAvailableCashSettings,
    googleTagManager: {
        enabled: false
    },
    raygun: {
        enabled: false,
        raygunApiKey: ""
    },
    universalLogin : {
        enabled: false,
    },
    footer: defaultFooterSettings,
    header: defaultHeaderSettings,
    deposits: { enabledManagers: [] },
    transfers: defaultTransferFeatureSetting,
    publicHeader: {
        enabled: false
    }
}